import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiSudoku } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const EMPTY_GRID  = () => Array.from({ length: 9 }, () => Array(9).fill(''));
const EMPTY_GIVEN = () => Array.from({ length: 9 }, () => Array(9).fill(false));

const PUZZLES = [
  {
    name: 'Easy',
    emoji: '😊',
    grid: [
      [5,3,0,0,7,0,0,0,0],
      [6,0,0,1,9,5,0,0,0],
      [0,9,8,0,0,0,0,6,0],
      [8,0,0,0,6,0,0,0,3],
      [4,0,0,8,0,3,0,0,1],
      [7,0,0,0,2,0,0,0,6],
      [0,6,0,0,0,0,2,8,0],
      [0,0,0,4,1,9,0,0,5],
      [0,0,0,0,8,0,0,7,9],
    ],
  },
  {
    name: 'Medium',
    emoji: '🤔',
    grid: [
      [0,0,0,2,6,0,7,0,1],
      [6,8,0,0,7,0,0,9,0],
      [1,9,0,0,0,4,5,0,0],
      [8,2,0,1,0,0,0,4,0],
      [0,0,4,6,0,2,9,0,0],
      [0,5,0,0,0,3,0,2,8],
      [0,0,9,3,0,0,0,7,4],
      [0,4,0,0,5,0,0,3,6],
      [7,0,3,0,1,8,0,0,0],
    ],
  },
  {
    name: 'Hard',
    emoji: '😤',
    grid: [
      [0,0,0,0,0,0,0,0,0],
      [0,0,0,0,0,3,0,8,5],
      [0,0,1,0,2,0,0,0,0],
      [0,0,0,5,0,7,0,0,0],
      [0,0,4,0,0,0,1,0,0],
      [0,9,0,0,0,0,0,0,0],
      [5,0,0,0,0,0,0,7,3],
      [0,0,2,0,1,0,0,0,0],
      [0,0,0,0,4,0,0,0,9],
    ],
  },
];

function computeConflicts(grid) {
  const c = Array.from({ length: 9 }, () => Array(9).fill(false));
  for (let r = 0; r < 9; r++) {
    for (let col = 0; col < 9; col++) {
      const v = grid[r][col];
      if (!v) continue;
      for (let k = 0; k < 9; k++) {
        if (k !== col && grid[r][k] === v) { c[r][col] = true; c[r][k] = true; }
        if (k !== r   && grid[k][col] === v) { c[r][col] = true; c[k][col] = true; }
      }
      const br = 3 * Math.floor(r / 3), bc = 3 * Math.floor(col / 3);
      for (let rr = br; rr < br + 3; rr++) {
        for (let cc = bc; cc < bc + 3; cc++) {
          if ((rr !== r || cc !== col) && grid[rr][cc] === v) {
            c[r][col] = true; c[rr][cc] = true;
          }
        }
      }
    }
  }
  return c;
}

export default function SudokuSolver() {
  const [grid, setGrid]         = useState(EMPTY_GRID);
  const [given, setGiven]       = useState(EMPTY_GIVEN);
  const [solved, setSolved]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [conflicts, setConflicts] = useState(() => Array.from({ length: 9 }, () => Array(9).fill(false)));
  const [activePuzzle, setActivePuzzle] = useState(null);

  function loadPuzzle(puzzle, idx) {
    const g  = puzzle.grid.map(row => row.map(v => v === 0 ? '' : String(v)));
    const gv = puzzle.grid.map(row => row.map(v => v !== 0));
    setGrid(g); setGiven(gv); setSolved(null);
    setError(''); setSelected(null); setActivePuzzle(idx);
    setConflicts(computeConflicts(g.map(row => row.map(v => v || ''))));
  }

  function clearAll() {
    setGrid(EMPTY_GRID()); setGiven(EMPTY_GIVEN());
    setSolved(null); setError(''); setSelected(null); setActivePuzzle(null);
    setConflicts(Array.from({ length: 9 }, () => Array(9).fill(false)));
  }

  const handleNumberInput = useCallback((num) => {
    if (!selected || solved) return;
    const { r, c } = selected;
    if (given[r][c]) return;
    const g = grid.map(row => [...row]);
    g[r][c] = num === 0 ? '' : String(num);
    setGrid(g);
    setSolved(null);
    setConflicts(computeConflicts(g.map(row => row.map(v => v || ''))));
  }, [selected, given, grid, solved]);

  useEffect(() => {
    function onKey(e) {
      if (!selected) return;
      if (e.key >= '1' && e.key <= '9') { handleNumberInput(parseInt(e.key)); return; }
      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') { handleNumberInput(0); return; }
      if (e.key === 'ArrowUp')    setSelected(s => s && s.r > 0 ? { r: s.r - 1, c: s.c } : s);
      if (e.key === 'ArrowDown')  setSelected(s => s && s.r < 8 ? { r: s.r + 1, c: s.c } : s);
      if (e.key === 'ArrowLeft')  setSelected(s => s && s.c > 0 ? { r: s.r, c: s.c - 1 } : s);
      if (e.key === 'ArrowRight') setSelected(s => s && s.c < 8 ? { r: s.r, c: s.c + 1 } : s);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, handleNumberInput]);

  async function handleSolve() {
    const numGrid = grid.map(row => row.map(v => v === '' ? 0 : parseInt(v)));
    setLoading(true); setError(''); setSolved(null);
    try {
      const { data } = await apiSudoku(numGrid);
      if (data.solved) { setSolved(data.grid); setSelected(null); }
      else setError(data.error || 'No solution found for this puzzle.');
    } catch (err) {
      setError(err.response?.data?.error || 'Cannot connect to server. Is the backend running?');
    } finally { setLoading(false); }
  }

  const hasConflicts = conflicts.some(row => row.some(Boolean));
  const filledCount  = grid.reduce((n, row) => n + row.filter(Boolean).length, 0);

  return (
    <div className="game-page">
      <PageSEO page="sudoku" />
      <div className="container" style={{ maxWidth: 560 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔢</span>
          <h1>Sudoku Solver</h1>
          <p>Load a puzzle or type in your own — then hit Solve!</p>
        </div>

        <div className="solver-card">

          {/* ── Puzzle selector ─────────────────────────────────────────── */}
          <div className="mb-4">
            <p className="fw-bold mb-2" style={{ color: 'var(--muted)', fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: 1 }}>
              Load a Sample Puzzle
            </p>
            <div className="d-flex gap-2 flex-wrap">
              {PUZZLES.map((p, idx) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => loadPuzzle(p, idx)}
                  style={{
                    borderRadius: 50, padding: '0.4rem 1.2rem',
                    border: `2px solid ${activePuzzle === idx ? 'var(--primary)' : 'var(--border)'}`,
                    background: activePuzzle === idx ? 'var(--primary)' : '#fff',
                    color: activePuzzle === idx ? '#fff' : 'var(--text)',
                    fontWeight: 800, fontFamily: 'Nunito,sans-serif', fontSize: '0.92rem',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >{p.emoji} {p.name}</button>
              ))}
              <button
                type="button"
                onClick={clearAll}
                style={{
                  borderRadius: 50, padding: '0.4rem 1rem',
                  border: '2px solid var(--border)', background: '#fff',
                  color: 'var(--muted)', fontWeight: 800, fontFamily: 'Nunito,sans-serif',
                  fontSize: '0.92rem', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >🗑 Clear</button>
            </div>
          </div>

          {/* ── 9×9 Board ────────────────────────────────────────────────── */}
          <div className="sudoku-board-outer">
            <table className="sudoku-table">
              <tbody>
                {Array.from({ length: 9 }, (_, r) => (
                  <tr key={r}>
                    {Array.from({ length: 9 }, (_, c) => {
                      const isGiven    = given[r][c];
                      const isSel      = selected?.r === r && selected?.c === c;
                      const isSameBox  = selected && Math.floor(selected.r/3)===Math.floor(r/3) && Math.floor(selected.c/3)===Math.floor(c/3);
                      const isSameLine = selected && (selected.r === r || selected.c === c);
                      const isConflict = conflicts[r][c] && !isGiven;
                      const isSolved   = !!solved && !isGiven;
                      const val        = solved ? (solved[r][c] === 0 ? '' : solved[r][c]) : (grid[r][c] || '');

                      let bg = '#fff';
                      if (isSel)          bg = '#c4b5fd';
                      else if (isConflict) bg = '#fecaca';
                      else if (isGiven)    bg = '#f1f5f9';
                      else if (isSolved)   bg = '#d1fae5';
                      else if (isSameBox)  bg = '#f5f3ff';
                      else if (isSameLine) bg = '#faf5ff';

                      return (
                        <td
                          key={c}
                          className="sudoku-td"
                          style={{
                            borderRight:  c === 2 || c === 5 ? '3px solid #6366f1' : '1px solid #cbd5e1',
                            borderBottom: r === 2 || r === 5 ? '3px solid #6366f1' : '1px solid #cbd5e1',
                            borderLeft:   c === 0 ? 'none' : undefined,
                            borderTop:    r === 0 ? 'none' : undefined,
                          }}
                          onClick={() => !solved && setSelected({ r, c })}
                        >
                          <div
                            className="sudoku-cell-box"
                            style={{
                              background: bg,
                              color: isConflict ? '#dc2626'
                                   : isGiven    ? '#1e1b4b'
                                   : isSolved   ? '#065f46'
                                   :              '#6366f1',
                              fontWeight: isGiven ? 900 : 700,
                              cursor: solved || isGiven ? 'default' : 'pointer',
                            }}
                          >
                            {val}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Progress bar ─────────────────────────────────────────────── */}
          <div className="d-flex align-items-center gap-2 mt-3">
            <small className="fw-bold text-muted" style={{ minWidth: 60 }}>{filledCount}/81</small>
            <div className="flex-grow-1" style={{ height: 8, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 10, transition: 'width 0.3s',
                width: `${(filledCount/81)*100}%`,
                background: filledCount === 81 ? 'var(--success)' : 'var(--primary)',
              }} />
            </div>
            <small className="fw-bold text-muted">{Math.round((filledCount/81)*100)}%</small>
          </div>

          {/* ── Number Pad ───────────────────────────────────────────────── */}
          {!solved && (
            <div className="sudoku-numpad mt-3">
              {[1,2,3,4,5,6,7,8,9].map(n => (
                <button
                  key={n}
                  className="snp-btn"
                  type="button"
                  disabled={!selected || (selected && given[selected.r][selected.c])}
                  onClick={() => handleNumberInput(n)}
                >{n}</button>
              ))}
              <button
                className="snp-btn snp-erase"
                type="button"
                disabled={!selected || (selected && given[selected.r][selected.c])}
                onClick={() => handleNumberInput(0)}
                title="Erase"
              >✕</button>
            </div>
          )}

          <p className="text-center mt-2 mb-0" style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600 }}>
            {solved ? '' : 'Click a cell · type 1–9 · arrow keys to navigate'}
          </p>

          {/* ── Alerts ───────────────────────────────────────────────────── */}
          {hasConflicts && !solved && (
            <div className="alert alert-custom mt-3 mb-0" style={{ background: '#fee2e2', color: '#991b1b' }}>
              ⚠️ Conflicts highlighted in red — fix them before solving.
            </div>
          )}
          {error && <div className="alert alert-danger alert-custom mt-3 mb-0">{error}</div>}

          {/* ── Action button ─────────────────────────────────────────────── */}
          <div className="mt-3">
            {solved ? (
              <div className="text-center">
                <div className="alert alert-custom mb-3" style={{ background: '#d1fae5', color: '#065f46' }}>
                  🎉 Solved! The green cells show the filled-in answers.
                </div>
                <button className="solve-btn" onClick={clearAll}>🗑 Clear &amp; Try Another</button>
              </div>
            ) : (
              <button
                className="solve-btn"
                onClick={handleSolve}
                disabled={loading || hasConflicts}
              >
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</>
                  : '🔢 Solve Puzzle'}
              </button>
            )}
          </div>

        </div>
      <SeoContent game="sudoku" />
      </div>
    </div>
  );
}
